import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { Button, Col } from 'antd';
import ReduxDataTable from '../redux_data_table/ReduxDataTable';
import { toolsRestCrudThunksForSupplier } from '../../thunks/tools_thunks';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getContactName } from '../../utils/DataFormatterUtils';
import { EmptyState } from '../empty_state/EmptyState';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import AddToolModal from './AddToolModal';
import PickUpModal from './PickUpModal';
import DropOffModal from './DropOffModal';
import DropOffConfirmationModal from './DropOffConfirmationModal';
import DateTimeHover from '../date_time_component/DateTime';
import { FILTER_FIELD_TYPE } from '../../utils/DataConstants';
import { stockLocationsRestCrudThunksForSupplier } from '../../thunks/stock_locations_thunks';

const TOOLS_INDEX_TARGET_COLLECTION = 'toolsIndex';

const TOOLS_ADVANCED_FILTERS_TC = 'toolsAdvancedFilters';

const ToolsIndexPage: FC<any> = ({
	history,
	location,
	tools,
	fetchTools,
	stockLocations,
	fetchStockLocations,
	fetchMultipleStockLocations,
}): React.ReactElement => {
	const currentUser = useSelector((state: any) => state.session.currentUser);

	const [addToolModalVisible, setAddToolModalVisible] = useState(false);
	const [pickUpTool, setPickUpTool] = useState(null);
	const [dropOffTool, setDropOffTool] = useState(null);
	const [dropOffConfirmationType, setDropOffConfirmationType] = useState(null);

	const filterConfig = useMemo(
		() => [
			{
				label: 'Status',
				fieldName: 'status',
				type: FILTER_FIELD_TYPE.RADIO_GROUP,
				items: [
					{ label: 'All', value: 'undefined' },
					{ label: 'Available', value: 'Available' },
					{ label: 'In Use', value: 'InUse' },
				],
				defaultValue: undefined,
			},
			{
				label: 'Stock Location',
				fieldName: 'stockLocationId',
				stateSlice: stockLocations,
				targetCollectionName: TOOLS_ADVANCED_FILTERS_TC,
				fetch: fetchStockLocations,
				fetchMultiple: fetchMultipleStockLocations,
			},
		],
		[stockLocations, fetchStockLocations, fetchMultipleStockLocations]
	);

	const handlePickUp = useCallback((tool) => {
		setPickUpTool(tool);
	}, []);

	const handleDropOff = useCallback(
		(tool) => {
			const checkout = nullSafeGet('currentCheckout', tool);
			const pickedUpBy = nullSafeGet('pickedUpBy', checkout);
			const pickedUpFor = nullSafeGet('pickedUpFor', checkout);
			const currentEmail = nullSafeGet('email', currentUser);

			if (pickedUpBy === currentEmail && pickedUpFor === currentEmail) {
				setDropOffTool(tool);
			} else if (pickedUpBy !== currentEmail) {
				setDropOffTool(tool);
				setDropOffConfirmationType('someoneElse');
			} else if (pickedUpBy === currentEmail && pickedUpFor !== currentEmail) {
				setDropOffTool(tool);
				setDropOffConfirmationType('youForSomeoneElse');
			}
		},
		[currentUser]
	);

	const refreshTools = useCallback(() => {
		const pagination = nullSafeGetOrElse(`${TOOLS_INDEX_TARGET_COLLECTION}.pagination`, tools, {});
		const sorting = nullSafeGetOrElse(`${TOOLS_INDEX_TARGET_COLLECTION}.sorting`, tools, {});
		const filters = nullSafeGetOrElse(`${TOOLS_INDEX_TARGET_COLLECTION}.filters`, tools, {});
		fetchTools(null, TOOLS_INDEX_TARGET_COLLECTION, pagination, sorting, filters);
	}, [tools, fetchTools]);

	const onRow = useCallback(
		(record) => ({
			onClick: () => {
				const path = `/supplier/tools/detail/${nullSafeGet('id', record)}/details`;
				history.push(
					getLinkWIthBackLinkParams(location, 'Back to Tools', path, {
						preserve: true,
						targetCollectionName: TOOLS_INDEX_TARGET_COLLECTION,
					})
				);
			},
		}),
		[history, location]
	);

	const getDropOffConfirmationContactName = useCallback(() => {
		if (!dropOffTool) return '';
		return getContactName(nullSafeGet('currentCheckout.pickedUpForContact', dropOffTool)).trim();
	}, [dropOffTool]);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'name',
				render: (text, record) => <span className="bodymedium">{record.name}</span>,
			},
			{
				title: 'Location',
				dataIndex: 'stockLocation',
				render: (text, record) => (
					<span className="bodyregular">
						{nullSafeGetOrElse('stockLocation.name', record, '--')}
					</span>
				),
			},
			{
				title: 'Status',
				dataIndex: 'status',
				render: (text, record) => {
					if (record.status === 'Available') {
						return (
							<span className="bodymedium" style={{ color: '#389e0d' }}>
								Available
							</span>
						);
					}
					const contactName =
						getContactName(nullSafeGet('currentCheckout.pickedUpForContact', record)).trim() ||
						'--';
					const pickedUpAt = nullSafeGet('currentCheckout.pickedUpAt', record);
					const timeZoneId = nullSafeGet('stockLocation.timeZoneId', record);
					return (
						<div>
							<span className="bodymedium" style={{ color: '#d4a843' }}>
								In Use
							</span>
							<span className="footnoteregular ml-2 text-character-secondary">
								Picked up by {contactName}
								{pickedUpAt && (
									<>
										{' at '}
										<DateTimeHover
											timestamp={pickedUpAt}
											timeZone={timeZoneId}
											dateFormat="h:mma z"
											showDate
										/>
									</>
								)}
							</span>
						</div>
					);
				},
			},
			{
				title: 'Action',
				dataIndex: 'action',
				render: (text, record) => {
					if (record.status === 'Available') {
						return (
							<Button
								onClick={(event) => {
									event.stopPropagation();
									handlePickUp(record);
								}}
							>
								Pick Up
							</Button>
						);
					}
					return (
						<Button
							onClick={(event) => {
								event.stopPropagation();
								handleDropOff(record);
							}}
						>
							Drop Off
						</Button>
					);
				},
			},
		],
		[handlePickUp, handleDropOff]
	);

	return (
		<>
			<ReduxDataTable
				entityCollectionName="tools"
				targetCollectionName={TOOLS_INDEX_TARGET_COLLECTION}
				fetchData={toolsRestCrudThunksForSupplier}
				stateSlice={tools}
				tableColumns={columns}
				filterConfig={filterConfig}
				keyAccessor={(el) => el.id}
				showHeader={false}
				onTableRow={onRow}
				rightActions={
					<Col className="flex items-baseline" style={{ gap: 8 }}>
						<Button size="large" type="primary" onClick={() => setAddToolModalVisible(true)}>
							+ Add Tool
						</Button>
					</Col>
				}
				emptyState={
					<EmptyState
						headline="No tools found"
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									Add your first tool to get started.
								</div>
								<Button type="primary" onClick={() => setAddToolModalVisible(true)}>
									Add Tool
								</Button>
							</div>
						}
					/>
				}
			/>
			{addToolModalVisible && (
				<AddToolModal
					visible
					onCancel={() => setAddToolModalVisible(false)}
					onSuccess={() => {
						setAddToolModalVisible(false);
						refreshTools();
					}}
				/>
			)}
			{pickUpTool && (
				<PickUpModal
					visible
					tool={pickUpTool}
					onCancel={() => setPickUpTool(null)}
					onSuccess={() => {
						setPickUpTool(null);
						refreshTools();
					}}
				/>
			)}
			{dropOffConfirmationType && dropOffTool && (
				<DropOffConfirmationModal
					visible
					type={dropOffConfirmationType}
					contactName={getDropOffConfirmationContactName()}
					onCancel={() => {
						setDropOffConfirmationType(null);
						setDropOffTool(null);
					}}
					onConfirm={() => setDropOffConfirmationType(null)}
				/>
			)}
			{dropOffTool && !dropOffConfirmationType && (
				<DropOffModal
					visible
					tool={dropOffTool}
					onCancel={() => setDropOffTool(null)}
					onSuccess={() => {
						setDropOffTool(null);
						refreshTools();
					}}
				/>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	tools: state.tools,
	stockLocations: state.stock_locations,
});

const mapDispatchToProps = (dispatch) => ({
	fetchTools: (params, targetCollectionName, pagination, sorting, filters, addToTargetCollection) =>
		dispatch(
			toolsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchStockLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			stockLocationsRestCrudThunksForSupplier.readLite(
				{ ...params, no_pagination: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleStockLocations: (ids, targetCollectionName) =>
		dispatch(stockLocationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
});

export default withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(ToolsIndexPage));
